import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// One scraper source and its runtime on/off state, as returned by GET /api/admin/sources.
export interface SourceToggle {
  id: string;
  enabled: boolean;
  // Per-row in-flight guard so a toggle disables only its own row while saving.
  saving?: boolean;
}

// Owns the admin source-management panel: loads every registered scraper source with its
// enabled flag and persists on/off changes to the server. Disabling a source only stops future
// scrapes; rows already stored for it stay visible. Mirrors the other singleton services — shared
// state lives here and the source-admin panel binds to it directly.
@singleton()
export class SourceAdminService {
  private readonly http: HttpClient = resolve(HttpClient);

  panelOpen: boolean = false;
  loading: boolean = false;
  error: string | null = null;
  sources: SourceToggle[] = [];

  // Loads the full source list with current enabled states. Safe to call whenever the panel is
  // opened; failures leave a readable error and an empty list.
  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const response = await this.http.get('/api/admin/sources');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: SourceToggle[] = await response.json();
      this.sources = data.map((s) => ({ id: s.id, enabled: s.enabled === true, saving: false }));
    } catch (e: any) {
      this.error = `Could not load sources: ${e.message}`;
      this.sources = [];
    } finally {
      this.loading = false;
    }
  }

  async openPanel(): Promise<void> {
    this.panelOpen = true;
    await this.load();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.error = null;
  }

  // Persists a single source's enabled flag. Optimistically flips the local state and rolls it
  // back on failure so the switch always reflects the server truth.
  async setEnabled(source: SourceToggle, enabled: boolean): Promise<void> {
    if (source.saving) return;
    const previous = source.enabled;
    source.saving = true;
    source.enabled = enabled;
    this.error = null;
    try {
      const response = await this.http.sendWithCsrf('PUT', `/api/admin/sources/${encodeURIComponent(source.id)}`, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    } catch (e: any) {
      source.enabled = previous;
      this.error = `Could not update ${source.id}: ${e.message}`;
    } finally {
      source.saving = false;
    }
  }

  toggle(source: SourceToggle): Promise<void> {
    return this.setEnabled(source, !source.enabled);
  }
}
